import React, { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * PlotTooltip — the ONE floating tooltip primitive shared across the DOM bar families.
 *
 * Replaces every scattered native `title=` attribute with a single token-styled floating
 * card (`bg-popover`/`text-popover-foreground`/`shadow-lg`/`rounded-md`) positioned above
 * the anchor. Per the `dataviz`/`interaction.md` spec the VALUE leads and the label
 * follows (e.g. "21% · Neither appealing nor unappealing").
 *
 * Focus parity: the tooltip shows on hover AND on keyboard focus of the anchor. The
 * trigger wrapper is focusable (`tabIndex={0}`) so keyboard users reach the same tooltip
 * a pointer hover reveals. Dependency-free — no Radix, simple absolute positioning inside
 * a relative wrapper.
 *
 * Alignment: `center` (default, used by every plot) centers a single-line card over the
 * anchor. `start` pins the card's left edge to the anchor's left edge and lets it wrap
 * within `min(18rem, 100vw - 2rem)`, for anchors near the left of a row of variable width.
 *
 * Side: `top` (default) opens the card above the anchor; `bottom` opens it below, for
 * anchors whose row above carries text the card must not cover.
 */
export interface PlotTooltipProps {
  /** Leading value (e.g. "21%") — rendered first, emphasized. */
  value: ReactNode
  /** Trailing label (e.g. "Neither appealing nor unappealing") — rendered after the value.
   *  Omit it for a single-sentence tooltip: the value then renders alone, with no separator. */
  label?: ReactNode
  /** Accessible name for the focusable trigger (it gets `role="img"` + this label), so a
   *  keyboard stop announces meaningfully instead of landing on an unnamed span. */
  triggerAriaLabel?: string
  /** Horizontal anchoring of the card. Defaults to `center`. */
  align?: 'center' | 'start'
  /** Vertical side the card opens on. Defaults to `top`. */
  side?: 'top' | 'bottom'
  /** Extra classes for the floating card only (opt-in; default tooltips are unchanged). */
  tooltipClassName?: string
  /** The anchor the tooltip describes. */
  children: ReactNode
}

export function PlotTooltip(props: PlotTooltipProps) {
  const { value, label, triggerAriaLabel, align = 'center', side = 'top', tooltipClassName, children } = props

  const [isOpen, setIsOpen] = useState(false)

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  return (
    <span
      className="relative inline-flex"
      tabIndex={0}
      role={triggerAriaLabel ? 'img' : undefined}
      aria-label={triggerAriaLabel}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}
      {isOpen && (
        <span
          role="tooltip"
          data-testid="qs-plot-tooltip"
          className={
            align === 'start'
              ? cn(
                  'pointer-events-none absolute left-0 z-50',
                  side === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
                  'w-max max-w-[min(18rem,calc(100vw-2rem))] whitespace-normal rounded-md bg-popover px-2 py-1 text-xs text-popover-foreground shadow-lg',
                  tooltipClassName,
                )
              : cn(
                  'pointer-events-none absolute left-1/2 z-50 -translate-x-1/2',
                  side === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
                  'whitespace-nowrap rounded-md bg-popover px-2 py-1 text-xs text-popover-foreground shadow-lg',
                  tooltipClassName,
                )
          }
        >
          {label == null ? (
            <span>{value}</span>
          ) : (
            <>
              <span className="font-medium">{value}</span>
              <span className="text-muted-foreground"> · </span>
              <span>{label}</span>
            </>
          )}
        </span>
      )}
    </span>
  )
}
